import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { FaArrowLeft, FaCheckCircle, FaLock } from 'react-icons/fa';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import PhoneVerification from '../components/auth/PhoneVerification';
import { API_URL } from '../config/api';
import { clearCart, selectCartCurrency, selectCartItems, selectCartTotal } from '../store/slices/cartSlice';

const CheckoutPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const cartItems = useSelector(selectCartItems);
  const cartTotal = useSelector(selectCartTotal);
  const currency = useSelector(selectCartCurrency);
  const settings = useSelector((state) => state.settings.data);
  const tenant = useSelector((state) => state.tenant.tenant);

  const [loading, setLoading] = useState(false);
  const [orderPlaced, setOrderPlaced] = useState(false);
  const [orderNumber, setOrderNumber] = useState('');
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [showPhoneVerification, setShowPhoneVerification] = useState(false);
  const [firebaseIdToken, setFirebaseIdToken] = useState(null);
  const [razorpayKey, setRazorpayKey] = useState(null);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponDiscount, setCouponDiscount] = useState(0);
  const [validatingCoupon, setValidatingCoupon] = useState(false);
  const [formData, setFormData] = useState({
    phone: '',
    address: {
      street: '',
      city: '',
      state: '',
      zipCode: '',
      country: 'India'
    },
    notes: ''
  });


  // Load tenant on mount if not available (only once)
  useEffect(() => {
    const loadTenantIfNeeded = async () => {
      // Check for both _id and id (backend compatibility)
      const tenantId = tenant?._id || tenant?.id;
      if (!tenantId) {
        try {
          // Check localStorage first
          const stored = localStorage.getItem('tenant');
          if (stored) {
            const parsedTenant = JSON.parse(stored);
            const storedTenantId = parsedTenant?._id || parsedTenant?.id;
            if (storedTenantId) {
              // Normalize tenant ID
              const normalizedTenant = {
                ...parsedTenant,
                _id: storedTenantId
              };
              // Dispatch setTenant to update Redux state
              const { setTenant } = await import('../store/slices/tenantSlice');
              dispatch(setTenant(normalizedTenant));
              return;
            }
          }
          
          // If not in localStorage, load from API
          const { loadTenant } = await import('../store/slices/tenantSlice');
          await dispatch(loadTenant());
        } catch (error) {
          console.error('[Checkout] Error loading tenant:', error);
        }
      }
    };
    
    loadTenantIfNeeded();
  }, [dispatch, tenant?._id, tenant?.id]); // Check both _id and id

  useEffect(() => {
    if (cartItems.length === 0 && !orderPlaced) {
      toast.info('Your cart is empty');
      navigate('/products');
    }
  }, [cartItems, navigate, orderPlaced]);

  // Load Razorpay key and payment mode from tenant settings API (only once when tenant is available)
  useEffect(() => {
    // Skip if Razorpay key is already loaded
    if (razorpayKey) {
      return;
    }

    const loadRazorpayConfig = async () => {
      // Get tenant from Redux or localStorage fallback
      let tenantToUse = tenant;
      const tenantId = tenantToUse?._id || tenantToUse?.id;
      if (!tenantId) {
        try {
          const stored = localStorage.getItem('tenant');
          if (stored) {
            tenantToUse = JSON.parse(stored);
          }
        } catch (err) {
          console.error('[Checkout] Error reading tenant from localStorage:', err);
        }
      }
      
      // Check for both _id and id
      const finalTenantId = tenantToUse?._id || tenantToUse?.id;
      if (!finalTenantId) {
        console.warn('[Checkout] Tenant not available, cannot load Razorpay config');
        return;
      }
      
      // Normalize tenant ID - create new object to avoid modifying frozen Redux state
      if (!tenantToUse._id) {
        tenantToUse = { ...tenantToUse, _id: finalTenantId };
      }

      try {
        console.log('[Checkout] Fetching Razorpay config for tenant:', tenantToUse.subdomain || tenantToUse._id);
        
        // Fetch tenant-specific settings
        const response = await axios.get(`${API_URL}/api/public/settings`, {
          headers: {
            'X-Tenant-Identifier': tenantToUse.subdomain || tenantToUse._id
          }
        });
        
        console.log('[Checkout] Settings API response:', {
          success: response.data.success,
          hasData: !!response.data.data,
          hasPaymentApi: !!response.data.data?.paymentApi
        });
        
        const paymentApi = response.data.data?.paymentApi;
        const keyId = paymentApi?.razorpayKeyId || process.env.REACT_APP_RAZORPAY_KEY_ID || '';
        const paymentMode = paymentApi?.razorpayMode || 'test';
        
        console.log('[Checkout] Payment API config extracted:', {
          hasKeyId: !!keyId,
          keyIdLength: keyId.length,
          keyIdPrefix: keyId ? keyId.substring(0, 7) : 'NONE',
          paymentMode,
          hasPaymentApi: !!paymentApi,
          paymentApiKeys: paymentApi ? Object.keys(paymentApi) : []
        });
        
        // Only set Razorpay key if it's configured and valid
        if (keyId && keyId.trim().length > 0) {
          // Validate key format (should start with 'rzp_')
          if (keyId.startsWith('rzp_')) {
            setRazorpayKey(keyId);
            console.log('[Checkout] ✅ Razorpay configured successfully - Key:', keyId.substring(0, 7) + '...', 'Mode:', paymentMode);
          } else {
            console.warn('[Checkout] ⚠️ Invalid Razorpay key format. Key should start with "rzp_", got:', keyId.substring(0, 10));
            setRazorpayKey(null);
          }
        } else {
          console.warn('[Checkout] ⚠️ Razorpay key not configured for tenant. Key ID is empty or missing.');
          setRazorpayKey(null);
        }
      } catch (error) {
        console.error('[Checkout] ❌ Error loading Razorpay config from tenant settings:', error);
        console.error('[Checkout] Error details:', {
          message: error.message,
          status: error.response?.status,
          statusText: error.response?.statusText,
          data: error.response?.data
        });
        // Fallback to environment variable if settings endpoint fails
        const envKey = process.env.REACT_APP_RAZORPAY_KEY_ID || '';
        if (envKey && envKey.startsWith('rzp_')) {
          setRazorpayKey(envKey);
          console.log('[Checkout] Using fallback Razorpay key from environment');
        } else {
          setRazorpayKey(null);
          console.warn('[Checkout] No valid Razorpay key found in environment variables either');
        }
      }
    };
    
    // Load immediately if tenant is available, otherwise wait a bit
    const tenantId = tenant?._id || tenant?.id;
    if (tenantId) {
      loadRazorpayConfig();
    } else {
      // Try loading after a short delay in case tenant is still loading
      const timer = setTimeout(() => {
        loadRazorpayConfig();
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [tenant?._id, tenant?.id, razorpayKey]); // Depend on tenant._id/id and razorpayKey to prevent loops

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name.startsWith('address.')) {
      const addressField = name.split('.')[1];
      setFormData({
        ...formData,
        address: {
          ...formData.address,
          [addressField]: value
        }
      });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  const calculateSubtotal = () => {
    return cartItems.reduce((sum, item) => {
      const itemPrice = item.product.discountedPrice || item.product.price;
      return sum + (itemPrice * item.quantity);
    }, 0);
  };

  const calculateShipping = () => {
    const shippingConfig = settings?.shipping;
    if (!shippingConfig) {
      // Fallback to default
      return cartTotal >= 5000 ? 0 : 100;
    }

    const pricingRules = shippingConfig.pricingRules || {};
    const freeShippingThreshold = pricingRules.freeShippingThreshold || 5000;
    
    // Check free shipping threshold first
    if (cartTotal >= freeShippingThreshold) {
      return 0;
    }

    let shippingPrice = pricingRules.basePrice || 100;

    // Calculate total weight and dimensions from cart items
    let totalWeight = 0;
    let maxTotalDimension = 0;

    cartItems.forEach(item => {
      const product = item.product;
      const quantity = item.quantity;
      
      // Calculate weight
      if (product.weight) {
        totalWeight += (product.weight * quantity);
      }
      
      // Calculate max total dimension (L + W + H)
      if (product.dimensions) {
        const dim = product.dimensions;
        const totalDim = (dim.length || 0) + (dim.width || 0) + (dim.height || 0);
        if (totalDim > maxTotalDimension) {
          maxTotalDimension = totalDim;
        }
      }
    });

    // Apply weight-based pricing if enabled
    if (pricingRules.weightBased?.enabled && totalWeight > 0) {
      const weightPrice = totalWeight * (pricingRules.weightBased.pricePerKg || 50);
      if (weightPrice > shippingPrice) {
        shippingPrice = weightPrice;
      }
    }

    // Apply size-based pricing if enabled
    if (pricingRules.sizeBased?.enabled && maxTotalDimension > 0) {
      // Sort tiers by maxDimension (ascending) and find first matching tier
      const tiers = [...(pricingRules.sizeBased.tiers || [])].sort((a, b) => 
        (a.maxDimension || 0) - (b.maxDimension || 0)
      );
      
      for (const tier of tiers) {
        if (maxTotalDimension <= (tier.maxDimension || 0)) {
          if (tier.price > shippingPrice) {
            shippingPrice = tier.price;
          }
          break;
        }
      }
    }

    return Math.round(shippingPrice);
  };

  const calculateTax = () => {
    // 18% GST
    return Math.round(cartTotal * 0.18);
  };

  const calculateTotal = () => {
    return cartTotal + calculateShipping() + calculateTax() - couponDiscount;
  };

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) {
      toast.error('Please enter a coupon code');
      return;
    }

    const tenantId = tenant?._id || tenant?.id;
    if (!tenantId) {
      toast.error('Tenant information not available');
      return;
    }

    setValidatingCoupon(true);
    try {
      const subtotal = calculateSubtotal();
      const productIds = cartItems.map(item => item.product._id);
      const categoryIds = cartItems.map(item => item.product.category?._id || item.product.category).filter(Boolean);

      const response = await axios.post(`${API_URL}/api/coupons/public/validate`, {
        code: couponCode.trim(),
        cartTotal: subtotal,
        customerEmail: formData.email || null,
        productIds,
        categoryIds,
        tenantId: tenant._id || tenant.id
      });

      if (response.data.success) {
        setAppliedCoupon(response.data.data.coupon);
        setCouponDiscount(response.data.data.discount);
        toast.success(response.data.data.message || 'Coupon applied successfully!');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Invalid or expired coupon code');
      setCouponCode('');
      setAppliedCoupon(null);
      setCouponDiscount(0);
    } finally {
      setValidatingCoupon(false);
    }
  };

  const handleRemoveCoupon = () => {
    setCouponCode('');
    setAppliedCoupon(null);
    setCouponDiscount(0);
    toast.info('Coupon removed');
  };

  // Handle phone verification success
  const handlePhoneVerificationSuccess = async (idToken, firebaseUser) => {
    setFirebaseIdToken(idToken);
    setPhoneVerified(true);
    setShowPhoneVerification(false);
    
    // Verify token with backend and optionally create/update user
    try {
      await axios.post(`${API_URL}/api/firebase-auth/verify-token`, {
        idToken
      });
    } catch (error) {
      console.error('Error verifying token with backend:', error);
      // Continue anyway - verification is done on frontend
    }
  };

  // Handle phone number change - reset verification
  const handlePhoneChange = (e) => {
    const newPhone = e.target.value;
    const oldPhone = formData.phone;
    setFormData({ ...formData, phone: newPhone });
    if (phoneVerified && newPhone !== oldPhone) {
      setPhoneVerified(false);
      setFirebaseIdToken(null);
      setShowPhoneVerification(false);
    }
  };

  // Phone verification is now optional - removed auto-trigger

  const initiatePayment = async (orderId, orderTotal, razorpayKeyToUse = null, tenantForRequest = null) => {
    const keyToUse = razorpayKeyToUse || razorpayKey;
    
    if (!window.Razorpay) {
      toast.error('Payment gateway script not loaded. Please refresh the page.');
      return false;
    }
    
    if (!keyToUse) {
      toast.error('Payment gateway key not available. Please contact support.');
      return false;
    }

    try {
      // Get tenant identifier for the request
      const tenantIdentifier = tenantForRequest?.subdomain || tenantForRequest?._id || tenantForRequest?.id || tenant?.subdomain || tenant?._id || tenant?.id;
      
      // Create Razorpay order (no auth required - public endpoint)
      const orderResponse = await axios.post(`${API_URL}/api/razorpay/create-order`, {
        amount: orderTotal,
        currency: currency || 'INR',
        receipt: `order_${orderId}`,
        notes: {
          orderId: orderId,
          type: 'product_order'
        }
      }, {
        headers: {
          ...(tenantIdentifier && { 'X-Tenant-Identifier': tenantIdentifier })
        }
      });

      if (!orderResponse.data.success || !orderResponse.data.data) {
        toast.error(orderResponse.data.message || 'Failed to initialize payment');
        return false;
      }

      const { orderId: razorpayOrderId, amount, currency: orderCurrency } = orderResponse.data.data;

      if (!razorpayOrderId) {
        toast.error('Invalid payment order. Please try again.');
        return false;
      }

      // Update the system order with the Razorpay order ID
      try {
        await axios.put(`${API_URL}/api/orders/${orderId}`, {
          razorpayOrderId: razorpayOrderId
        }, {
          headers: {
            ...(tenantIdentifier && { 'X-Tenant-Identifier': tenantIdentifier })
          }
        });
        console.log('[Checkout] ✅ Updated order with Razorpay order ID:', razorpayOrderId);
      } catch (updateError) {
        console.error('[Checkout] Error updating order with Razorpay ID:', updateError);
        // Continue even if update fails
      }

      // Razorpay options
      const options = {
        key: keyToUse,
        amount: amount, // Amount in paise
        currency: orderCurrency,
        name: settings?.companyName || 'Store',
        description: `Order Payment`,
        order_id: razorpayOrderId,
        handler: async (razorpayResponse) => {
          setProcessingPayment(true);
          try {
            // Verify payment - use tenantForRequest passed to initiatePayment, or fallback to Redux/localStorage
            let tenantForVerification = tenantForRequest || tenant;
            
            // If still not available, try to get from localStorage or Redux
            if (!tenantForVerification?._id && !tenantForVerification?.id) {
              try {
                const stored = localStorage.getItem('tenant');
                if (stored) {
                  tenantForVerification = JSON.parse(stored);
                }
              } catch (err) {
                console.error('[Checkout] Error reading tenant from localStorage in handler:', err);
              }
            }
            
            const tenantIdentifier = tenantForVerification?.subdomain || tenantForVerification?._id || tenantForVerification?.id;
            
            const verificationResponse = await axios.post(`${API_URL}/api/razorpay/verify-payment`, {
              razorpayOrderId: razorpayResponse.razorpay_order_id,
              razorpayPaymentId: razorpayResponse.razorpay_payment_id,
              razorpaySignature: razorpayResponse.razorpay_signature,
              orderId: orderId
            }, {
              headers: {
                ...(tenantIdentifier && { 'X-Tenant-Identifier': tenantIdentifier })
              }
            });

            if (verificationResponse.data.success) {
              const orderNumber = verificationResponse.data.data?.orderNumber || orderId;
              toast.success('Order successfully placed! Check your mail for order details.', {
                duration: 5000
              });
              
              setOrderNumber(orderNumber);
              setOrderPlaced(true);
              dispatch(clearCart());
              
              // Redirect to order confirmation or WhatsApp
              setTimeout(() => {
                const whatsappMessage = encodeURIComponent(
                  `Order Confirmation: ${orderId}\n\n` +
                  `Items:\n${cartItems.map(item => `- ${item.product.name} x${item.quantity}`).join('\n')}\n\n` +
                  `Total: ${currency === 'USD' ? '$' : currency === 'EUR' ? '€' : '₹'}${orderTotal.toLocaleString()}\n\n` +
                  `Customer: ${formData.name}\nEmail: ${formData.email}\nPhone: ${formData.phone}`
                );
                if (settings?.whatsappLink) {
                  window.open(`${settings.whatsappLink}?text=${whatsappMessage}`, '_blank');
                }
              }, 2000);
            } else {
              toast.error('Payment verification failed. Please contact support.');
            }
          } catch (error) {
            console.error('Payment verification error:', error);
            console.error('Error details:', {
              message: error.message,
              response: error.response?.data,
              status: error.response?.status
            });
            
            // Even if verification fails, check if order was created
            // The order might have been created before payment verification
            if (orderId) {
              console.log('Order was created with ID:', orderId);
              // Show a warning but don't block the user
              toast.warning('Payment processed, but verification had issues. Your order may have been placed. Please check your email or contact support.', {
                duration: 7000
              });
              // Still show success page if order exists
              setOrderNumber(orderId);
              setOrderPlaced(true);
              dispatch(clearCart());
            } else {
              toast.error(error.response?.data?.message || 'Payment verification failed. Please contact support.');
            }
          } finally {
            setProcessingPayment(false);
            setLoading(false);
          }
        },
        prefill: {
          name: formData.name.trim(),
          email: formData.email.trim(),
          contact: formData.phone.replace(/[\s-]/g, '')
        },
        theme: {
          color: '#8B4513'
        },
        modal: {
          ondismiss: () => {
            toast.info('Payment cancelled. Your order is still pending payment.');
            setProcessingPayment(false);
            setLoading(false);
          }
        }
      };

      const razorpay = new window.Razorpay(options);
      razorpay.on('payment.failed', (response) => {
        toast.error(`Payment failed: ${response.error.description || 'Unknown error'}`);
        setProcessingPayment(false);
        setLoading(false);
      });
      
      razorpay.open();
      setProcessingPayment(true);
      return true;
    } catch (error) {
      console.error('Payment initiation error:', error);
      
      // Handle specific error cases
      if (error.response) {
        const errorMessage = error.response.data?.message || error.response.data?.error || 'Failed to initiate payment';
        const statusCode = error.response.status;
        
        if (statusCode === 503) {
          toast.error('Payment gateway is not configured. Please contact admin.');
        } else if (statusCode === 400) {
          toast.error(errorMessage);
        } else if (statusCode === 500) {
          toast.error(errorMessage || 'Server error. Please try again later or contact support.');
        } else {
          toast.error(errorMessage);
        }
      } else if (error.request) {
        toast.error('Unable to connect to payment server. Please check your internet connection.');
      } else {
        toast.error(error.message || 'Failed to initiate payment. Please try again.');
      }
      
      setProcessingPayment(false);
      return false;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.phone || 
        !formData.address.street || !formData.address.city || 
        !formData.address.state || !formData.address.zipCode) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Phone verification is optional - removed requirement

    setLoading(true);

    // Get tenant for fetching Razorpay config - try to load if not available
    let tenantToUse = tenant;
    if (!tenantToUse?._id) {
      try {
        const stored = localStorage.getItem('tenant');
        if (stored) {
          tenantToUse = JSON.parse(stored);
        }
      } catch (err) {
        console.error('[Checkout] Error reading tenant from localStorage:', err);
      }
    }

    // If still not available, try to load from API
    if (!tenantToUse?._id) {
      try {
        console.log('[Checkout] Tenant not found, loading from API...');
        const { loadTenant } = await import('../store/slices/tenantSlice');
        const result = await dispatch(loadTenant());
        
        const loadedTenant = result.payload?.tenant;
        if (loadedTenant && (loadedTenant._id || loadedTenant.id)) {
          // Normalize tenant ID - use _id if available, otherwise use id
          tenantToUse = {
            ...loadedTenant,
            _id: loadedTenant._id || loadedTenant.id
          };
          console.log('[Checkout] ✅ Tenant loaded from API:', tenantToUse.name);
        } else {
          // Try to get tenant from URL path or subdomain
          const { fetchTenantInfo } = await import('../utils/tenantDetection');
          const fetchedTenant = await fetchTenantInfo();
          if (fetchedTenant && (fetchedTenant._id || fetchedTenant.id)) {
            // Normalize tenant ID
            tenantToUse = {
              ...fetchedTenant,
              _id: fetchedTenant._id || fetchedTenant.id
            };
            // Update Redux state
            const { setTenant } = await import('../store/slices/tenantSlice');
            dispatch(setTenant(tenantToUse));
            console.log('[Checkout] ✅ Tenant loaded from API directly:', tenantToUse.name);
          }
        }
      } catch (error) {
        console.error('[Checkout] Error loading tenant:', error);
      }
    }

    // Check for both _id and id (backend compatibility)
    const tenantId = tenantToUse?._id || tenantToUse?.id;
    if (!tenantId) {
      toast.error('Unable to load store information. Please refresh the page and try again.');
      setLoading(false);
      return;
    }
    
    // Ensure tenantToUse has _id for consistency - create new object to avoid modifying frozen Redux state
    if (!tenantToUse._id) {
      tenantToUse = { ...tenantToUse, _id: tenantId };
    }
    
    // Fetch Razorpay config from tenant settings if not already loaded
    let currentRazorpayKey = razorpayKey;
    if (!currentRazorpayKey) {
      try {
        console.log('[Checkout] Fetching Razorpay config on button click for tenant:', tenantToUse.subdomain || tenantToUse._id);
        
        const response = await axios.get(`${API_URL}/api/public/settings`, {
          headers: {
            'X-Tenant-Identifier': tenantToUse.subdomain || tenantToUse._id
          }
        });
        
        const paymentApi = response.data.data?.paymentApi;
        const keyId = paymentApi?.razorpayKeyId || process.env.REACT_APP_RAZORPAY_KEY_ID || '';
        
        if (keyId && keyId.trim().length > 0 && keyId.startsWith('rzp_')) {
          currentRazorpayKey = keyId;
          setRazorpayKey(keyId);
          console.log('[Checkout] ✅ Razorpay key loaded on button click');
        } else {
          toast.error('Payment gateway not configured. Please contact support or configure Razorpay in admin settings.');
          setLoading(false);
          return;
        }
      } catch (error) {
        console.error('[Checkout] ❌ Error fetching Razorpay config on button click:', error);
        toast.error('Failed to load payment gateway. Please try again or contact support.');
        setLoading(false);
        return;
      }
    }

    // Get tenant identifier for API requests
    const tenantIdentifier = tenantToUse?.subdomain || tenantToUse?._id || tenantToUse?.id;

    // Check if Razorpay script is loaded
    if (!window.Razorpay) {
      toast.error('Payment gateway script not loaded. Please refresh the page.');
      setLoading(false);
      return;
    }
    try {
      // Prepare order items
      const items = cartItems.map(item => ({
        product: item.product._id,
        name: item.product.name,
        quantity: item.quantity,
        price: item.product.price,
        currency: item.product.currency,
        discount: item.product.discount || 0,
        discountedPrice: item.product.discountedPrice || item.product.price
      }));

      const orderTotal = calculateTotal();

      const orderData = {
        customer: {
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          address: formData.address
        },
        items,
        subtotal: calculateSubtotal(),
        shipping: calculateShipping(),
        tax: calculateTax(),
        discount: 0,
        couponCode: appliedCoupon ? couponCode.trim().toUpperCase() : undefined,
        couponDiscount: couponDiscount || 0,
        total: orderTotal,
        currency,
        notes: formData.notes,
        paymentMethod: 'razorpay'
      };

      // Create order first
      const response = await axios.post(`${API_URL}/api/orders`, orderData, {
        headers: {
          ...(tenantIdentifier && { 'X-Tenant-Identifier': tenantIdentifier })
        }
      });
      const order = response.data.data;

      if (!order || !order._id) {
        toast.error('Failed to create order. Please try again.');
        setLoading(false);
        return;
      }

      // Initiate Razorpay payment (use currentRazorpayKey which was just fetched if needed)
      const paymentInitiated = await initiatePayment(order._id, orderTotal, currentRazorpayKey, tenantToUse);
      
      if (!paymentInitiated) {
        // Payment initiation failed, but order is created
        toast.warning('Order created but payment could not be initiated. Please contact support.');
        setLoading(false);
      }
      // If payment initiated successfully, the payment handler will handle success
    } catch (error) {
      console.error('Order error:', error);
      toast.error(error.response?.data?.message || 'Failed to place order. Please try again.');
      setLoading(false);
    }
  };

  const currencySymbol = currency === 'USD' ? '$' : currency === 'EUR' ? '€' : '₹';

  if (orderPlaced) {
    return (
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-2xl mx-auto text-center">
          <div className="bg-green-100 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6">
            <FaCheckCircle className="text-5xl text-green-600" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Order Placed Successfully!</h1>
          <p className="text-xl text-gray-600 mb-8">
            Your order number is: <strong className="text-primary">{orderNumber}</strong>
          </p>
          <p className="text-gray-700 mb-8">
            We've sent the order details to your WhatsApp. Our team will contact you shortly to confirm your order.
          </p>
          <div className="flex gap-4 justify-center">
            <button
              onClick={() => navigate('/products')}
              className="bg-primary text-white px-8 py-3 rounded-lg hover:bg-primary-dark transition font-semibold"
            >
              Continue Shopping
            </button>
            <button
              onClick={() => navigate('/')}
              className="bg-gray-200 text-gray-700 px-8 py-3 rounded-lg hover:bg-gray-300 transition font-semibold"
            >
              Go to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-2 text-gray-600 hover:text-primary mb-6 transition"
      >
        <FaArrowLeft />
        Back to Cart
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Checkout Form */}
        <div className="lg:col-span-2">
          <h1 className="text-3xl font-bold text-gray-900 mb-6">Checkout</h1>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Customer Information */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold mb-4">Customer Information</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number *
                    {phoneVerified && (
                      <span className="ml-2 text-green-600 text-xs">
                        ✓ Verified
                      </span>
                    )}
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handlePhoneChange}
                    required
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent ${
                      phoneVerified ? 'border-green-500 bg-green-50' : ''
                    }`}
                    placeholder="Enter 10-digit phone number"
                  />
                  {formData.phone && formData.phone.length >= 10 && !phoneVerified && (
                    <button
                      type="button"
                      onClick={() => setShowPhoneVerification(true)}
                      className="mt-2 text-sm text-primary hover:text-primary-dark font-medium"
                    >
                      {showPhoneVerification ? 'Hide Verification' : 'Verify Phone Number'}
                    </button>
                  )}
                </div>
                {showPhoneVerification && formData.phone && formData.phone.length >= 10 && !phoneVerified && (
                  <div className="col-span-2 mt-2">
                    <PhoneVerification
                      phoneNumber={formData.phone}
                      onVerificationSuccess={handlePhoneVerificationSuccess}
                      onCancel={() => setShowPhoneVerification(false)}
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Shipping Address */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold mb-4">Shipping Address</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Street Address *
                  </label>
                  <input
                    type="text"
                    name="address.street"
                    value={formData.address.street}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      City *
                    </label>
                    <input
                      type="text"
                      name="address.city"
                      value={formData.address.city}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      State *
                    </label>
                    <input
                      type="text"
                      name="address.state"
                      value={formData.address.state}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Zip Code *
                    </label>
                    <input
                      type="text"
                      name="address.zipCode"
                      value={formData.address.zipCode}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Country
                    </label>
                    <input
                      type="text"
                      name="address.country"
                      value={formData.address.country}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Order Notes */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold mb-4">Order Notes (Optional)</h2>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                rows="4"
                placeholder="Any special instructions or notes for your order..."
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>

            <button
              type="submit"
              disabled={loading || processingPayment}
              className="w-full bg-primary text-white py-4 rounded-lg hover:bg-primary-dark transition font-semibold text-lg flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {processingPayment ? (
                'Processing Payment...'
              ) : loading ? (
                'Loading Payment Gateway...'
              ) : (
                <>
                  <FaLock />
                  Pay & Place Order
                </>
              )}
            </button>
          </form>
        </div>

        {/* Order Summary */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-lg shadow-md p-6 sticky top-4">
            <h2 className="text-xl font-semibold mb-4">Order Summary</h2>
            
            {/* Cart Items */}
            <div className="space-y-3 mb-6 max-h-64 overflow-y-auto">
              {cartItems.map((item) => {
                const itemPrice = item.product.discountedPrice || item.product.price;
                const itemTotal = itemPrice * item.quantity;
                return (
                  <div key={item.product._id} className="flex gap-3 pb-3 border-b">
                    <img
                      src={item.product.images?.[0] || 'https://via.placeholder.com/100'}
                      alt={item.product.name}
                      className="w-16 h-16 object-cover rounded"
                    />
                    <div className="flex-1">
                      <h3 className="font-semibold text-sm line-clamp-2">{item.product.name}</h3>
                      <p className="text-xs text-gray-600">Qty: {item.quantity}</p>
                      <p className="text-sm font-semibold text-primary">
                        {currencySymbol}{itemTotal.toLocaleString()}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Coupon Code Section */}
            <div className="mb-4 pb-4 border-b">
              {!appliedCoupon ? (
                <div className="flex gap-2">
                  <input
                    type="text"
                    placeholder="Enter coupon code"
                    value={couponCode}
                    onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                    onKeyPress={(e) => e.key === 'Enter' && handleApplyCoupon()}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                  <button
                    type="button"
                    onClick={handleApplyCoupon}
                    disabled={validatingCoupon || !couponCode.trim()}
                    className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {validatingCoupon ? 'Applying...' : 'Apply'}
                  </button>
                </div>
              ) : (
                <div className="flex items-center justify-between bg-green-50 p-3 rounded-lg">
                  <div>
                    <p className="text-sm font-semibold text-green-800">
                      {appliedCoupon.name} ({appliedCoupon.code})
                    </p>
                    <p className="text-xs text-green-600">
                      {appliedCoupon.discountType === 'percentage' 
                        ? `${appliedCoupon.discountValue}% off` 
                        : `${currencySymbol}${appliedCoupon.discountValue} off`}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={handleRemoveCoupon}
                    className="text-red-600 hover:text-red-800 text-sm font-semibold"
                  >
                    Remove
                  </button>
                </div>
              )}
            </div>

            {/* Price Breakdown */}
            <div className="space-y-2 mb-4">
              <div className="flex justify-between text-gray-600">
                <span>Subtotal:</span>
                <span>{currencySymbol}{calculateSubtotal().toLocaleString()}</span>
              </div>
              <div className="flex justify-between text-gray-600">
                <span>Shipping:</span>
                <span>
                  {calculateShipping() === 0 ? (
                    <span className="text-green-600">Free</span>
                  ) : (
                    currencySymbol + calculateShipping().toLocaleString()
                  )}
                </span>
              </div>
              <div className="flex justify-between text-gray-600">
                <span>Tax (GST 18%):</span>
                <span>{currencySymbol}{calculateTax().toLocaleString()}</span>
              </div>
              {appliedCoupon && couponDiscount > 0 && (
                <div className="flex justify-between text-green-600 font-semibold">
                  <span>Coupon Discount ({appliedCoupon.code}):</span>
                  <span>-{currencySymbol}{couponDiscount.toLocaleString()}</span>
                </div>
              )}
              <div className="border-t pt-2 flex justify-between text-lg font-bold">
                <span>Total:</span>
                <span className="text-primary">{currencySymbol}{calculateTotal().toLocaleString()}</span>
              </div>
            </div>

            {calculateShipping() === 0 && (
              <p className="text-sm text-green-600 font-semibold mb-4">
                🎉 Free shipping on orders above {currencySymbol}5,000!
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;

